import { getTranslatedCollection } from 'ha/utils/urls/getTranslatedCollection';
import { CategoriesTranslations } from 'ha/models/Listing/translations';
import { IntlObject } from 'ha/i18n';
import { AppServices } from 'ha/services/getAppServices';
import { Listing } from '@ha/api/v2/types/Listing';
import { UnitType } from '@ha/api/v2/types/inventory/UnitType';
import { MakeCallPromise } from '@ha/api/v2/createMakeCall';
import { ConversationsResponse } from '@ha/api/v2/getConversations';
import { User } from '@ha/api/v2/types/User';
import { UserInformation } from '@ha/api/v2/types/UserInformation';
import { RentalConditions } from '@ha/api/v2/types/RentalConditions';
import { Conversation } from '@ha/api/v2/types';
import { RequestError } from '@ha/api/v2/errors';

import { getCategory } from './getCategory';
import { ListingEntity } from '../types';
import { LISTING_NOT_FOUND_ERROR_MESSAGE } from '../constants';

type UnitTypeExtended = WithRequiredProperties<
  UnitType,
  'photoURLList' | 'exclusions' | 'bookablePeriods' | 'videoURLList' | 'costs'
> & {
  unitTypeId: number;
};

type Inventory =
  | WithRequiredProperties<
      Listing,
      | 'photoURLList'
      | 'exclusions'
      | 'bookablePeriods'
      | 'videoURLList'
      | 'costs'
    >
  | UnitTypeExtended;

interface FetchResponse {
  listing: Inventory;
  advertiser: User;
  advertiserInformation: WithRequiredProperties<
    UserInformation,
    | 'listingsCount'
    | 'savedPaymentMethodsCount'
    | 'advertiserBookingsCount'
    | 'isKYCDone'
  >;
  rentalConditions: RentalConditions | null;
  lastConversation: Conversation | null;
  listingEntity: ListingEntity;
}

async function fetchListing(
  id: number,
  parameters: {
    startDate?: string;
    endDate?: string;
    useUnitType?: boolean;
  },
  {
    apiV2,
    intl,
  }: AppServices & {
    intl: IntlObject;
  },
  currentUserId: number,
): Promise<FetchResponse> {
  const { data: listing } = await (parameters.useUnitType
    ? (apiV2.getUnitTypeById(id, {
        expand: 'photos,exclusions,bookablePeriods,videos,costs,units',
        lang: intl.lang,
      }) as MakeCallPromise<FetchResponse['listing']>)
    : (apiV2.getListingById(id, {
        expand: 'photos,exclusions,bookablePeriods,videos,costs',
        lang: intl.lang,
      }) as MakeCallPromise<FetchResponse['listing']>)
  ).catch(error => {
    if (error.status === 404) {
      throw new Error(LISTING_NOT_FOUND_ERROR_MESSAGE);
    }

    throw error;
  });

  if (parameters.useUnitType) {
    listing.unitTypeId = listing.id;
  }

  const [
    { data: advertiser },
    { data: advertiserInformation },
    { data: conversations },
    { data: rentalConditions },
  ] = await Promise.all([
    apiV2.getUser(listing.advertiserId).catch(error => {
      if (error.status === 404) {
        throw new Error(LISTING_NOT_FOUND_ERROR_MESSAGE);
      }

      throw error;
    }),
    apiV2
      .getUserInformation(listing.advertiserId, {
        expand:
          'listingsCount,savedPaymentMethodsCount,advertiserBookingsCount,isKYCDone',
      })
      .catch(error => {
        if (error.status === 404) {
          throw new Error(LISTING_NOT_FOUND_ERROR_MESSAGE);
        }

        throw error;
      }) as MakeCallPromise<FetchResponse['advertiserInformation']>,
    currentUserId && currentUserId !== listing.advertiserId
      ? apiV2
          .getConversations('me', {
            [parameters.useUnitType ? 'unitTypeId' : 'listingId']: id,
            limit: 1,
          })
          .catch(error => {
            if (
              error.status === 404 ||
              error.status === 401 ||
              error.type === 'request-timeout'
            ) {
              return {
                data: { conversations: [], count: 0 } as ConversationsResponse,
              };
            }
            throw error;
          })
      : Promise.resolve({
          data: { conversations: [], count: 0 } as ConversationsResponse,
        }),
    parameters.startDate && parameters.endDate
      ? apiV2
          .getRentalConditionsEstimate(
            parameters.useUnitType ? { unitTypeId: id } : { listingId: id },
            {
              startDate: parameters.startDate,
              endDate: parameters.endDate,
            },
          )
          .catch(() => ({ data: null }))
      : Promise.resolve({ data: null }),
    (parameters.useUnitType
      ? apiV2.getQualificationRequirementsByUnitType(listing.uuid)
      : apiV2.getQualificationRequirementsByListing(listing.uuid)
    )
      .then(({ data }) => {
        listing.requiredDocuments = data?.requirements?.documents || [];
      })
      .catch(error => {
        const requestError = error as RequestError;
        if (requestError.status === 404) {
          return;
        }
        throw error;
      }),
  ]);

  const lastConversation = conversations.conversations[0] || null;

  const category = getCategory(listing.kind, listing.type);

  const propertyType =
    listing && 'unitTypeType' in listing
      ? listing?.unitTypeType
      : listing?.listingType;

  const listingEntity: ListingEntity = {
    ...listing,
    listingType: propertyType,
    entityId: String(listing.id),
    bookablePeriods: listing.bookablePeriods ?? [],
    searchURL: {
      name: intl.T(CategoriesTranslations[category]),
      path: intl.urlResolver.getSearchUrl(
        listing.cityLocalized || listing.city,
        listing.countryLocalized || listing.countryLocalized,
        {},
        getTranslatedCollection(intl.lang, category),
      ),
    },
    advertiser: {
      ...advertiser,
      ...advertiserInformation,
      entityId: String(advertiser.id),
      uniID: advertiser.uniId,
      registeredSince: advertiser.createdAt,
      hasPaymentMethod: advertiserInformation.savedPaymentMethodsCount > 0,
      completedBookingsCount: advertiserInformation.advertiserBookingsCount,
      lastMessage: {
        startDate: lastConversation?.rentalConditions?.startDate ?? '',
        endDate: lastConversation?.rentalConditions?.endDate ?? '',
        message:
          lastConversation?.messages && lastConversation.messageCount > 0
            ? lastConversation.messages[lastConversation.messages.length - 1]
                .text
            : '',
      },
    },
    rentalConditions,
    lastConversation,
  };

  return {
    listing,
    advertiser,
    advertiserInformation,
    rentalConditions,
    lastConversation,
    listingEntity,
  };
}

export { fetchListing };
